import React, {useState, useEffect, useContext, useCallback, useMemo, useRef} from "react";
import {useParams} from "react-router";
import {NavLink, Navigate, useLocation} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/free-solid-svg-icons";
import styled, {css} from "styled-components";

// Hooks
import useApi from "../hooks/useApi.js";
import useMountedState from "../hooks/useMountedState.js";

// Contexts
import {AuthContext} from "../contexts/auth.js";
import {FacilityNavContext} from "../contexts/facilitynav.js";
import {useSocket} from "../contexts/socket.js";
import {NotificationContext} from "../contexts/notify.js";

// Utils
import {exists} from "../utils/helpers.js";

// Components
import Room, {getFacilityRooms, LIST_LOCKS, REQUEST_LOCKS, ROOM_JOINED} from "./general/Room.js";
import FacilityPageHeader from "./general/FacilityPageHeader.js";
import Analytics from "./facility/Analytics.js";
import Checksheets from "./facility/Checksheets.js";
import Reports from "./facility/Reports.js";
import CustomTables from "./general/CustomTables.js";
import Contacts from "./facility/Contacts.js";
import Details from "./facility/Details.js";
import PrimaryAddress from "./facility/PrimaryAddress.js";
import TableFacilityUsers from "./facility/TableFacilityUsers.js";
import ContactsPage from "./facility/ContactsPage.js";
import Modal from "../components/Modal.js";
import ModalEditDetails from "./facility/ModalEditDetails.js";
import ModalEditContacts from "./facility/ModalEditContacts.js";

// Style
import {bp} from "../style/components/breakpoints.js";
import {flex, z} from "../style/components/mixins.js";
import {voice} from "../style/components/typography.js";
import {pad, border, radius, transition} from "../style/components/variables.js";
import {Loader, NotLoaded, Page} from "../style/components/general.js";

const FacilityDashboard = () => {
  const isMounted = useMountedState();
  const {slug} = useParams();
  const {pathname} = useLocation();

  const socket = useSocket();

  const {currentUser, roleCanAccessPage, roleCanAccessResource} = useContext(AuthContext);

  const timers = useRef({});

  useEffect(
    () => () => {
      Object.values(timers.current).forEach(timer => clearTimeout(timer));
    },
    []
  );
  const {getNotifications} = useContext(NotificationContext);
  const {removeOptions, facility, setFacility, setAnalytics} = useContext(FacilityNavContext);

  const {api: apiFacility} = useApi(`facilities/${slug}`);

  // Facility
  const [showDetails, setShowDetails] = useState(false);
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [showEditContacts, setShowEditContacts] = useState(false);
  const [active, setActive] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [locks, setLocks] = useState();
  const [actionMenuEl, setActionMenuEl] = useState(null);

  const tabPath = useMemo(
    () => pathname?.replace(`facilities/${slug}`, "")?.replace("/", ""),
    [pathname, slug]
  );

  const listLocks = useCallback(
    ({lockList, userId, fromPercentComplete}) => {
      if (
        !userId ||
        (userId === currentUser?.publicId && !fromPercentComplete) ||
        (userId !== currentUser?.publicId && fromPercentComplete)
      ) {
        setLocks(lockList);
      }
    },
    [currentUser?.publicId]
  );

  // Refresh lock state when each lock TTL expires so stale lock indicators are cleared.
  useEffect(() => {
    if (locks) {
      Object.keys(locks).forEach(key => {
        const lockInfo = locks[key];
        const {ttl} = lockInfo;
        if (exists(ttl) && ttl > 0 && ttl <= 3 * 60 * 60) {
          if (timers.current[key]) clearTimeout(timers.current[key]);
          const timer = setTimeout(() => {
            socket.emit(REQUEST_LOCKS, {
              rooms: getFacilityRooms(slug, "checksheet"),
              user: currentUser.publicId,
              type: "checksheet",
              to_sender: true
            });
            delete timers.current[key];
          }, ttl * 1000);
          timers.current[key] = timer;
        }
      });
    }
  }, [locks, socket, currentUser, slug]);

  // Socket Management - resource locking
  useEffect(() => {
    if (isMounted() && slug) {
      socket.on(LIST_LOCKS, listLocks);
    }

    return () => {
      socket.off(LIST_LOCKS, listLocks);
    };
  }, [isMounted, socket, slug, listLocks]);

  useEffect(() => {
    if (
      isMounted() &&
      slug &&
      currentUser?.publicId &&
      pathname === `/facilities/${slug}/checksheets`
    ) {
      socket.emit(REQUEST_LOCKS, {
        rooms: getFacilityRooms(slug, "checksheet"),
        user: currentUser.publicId,
        type: "checksheet",
        to_sender: true
      });
    }
  }, [isMounted, pathname, slug, currentUser, socket]);

  // Component unmount
  useEffect(
    () => () => {
      if (!isMounted()) removeOptions(["chart_builder", "stat_builder"]);
    },
    [isMounted, removeOptions]
  );

  const roomJoined = useCallback(({lockList}) => {
    setLocks(lockList);
  }, []);

  useEffect(() => {
    if (isMounted()) socket.on(ROOM_JOINED, roomJoined);

    return () => {
      socket.off(ROOM_JOINED, roomJoined);
    };
  }, [isMounted, roomJoined, socket]);

  const requestedSlug = useRef(null);

  useEffect(() => {
    // Skip slugs already requested so a stale navigation doesn't abort the in-flight fetch.
    if (currentUser?.publicId && facility === null && requestedSlug.current !== slug) {
      requestedSlug.current = slug;
      apiFacility.callGet().then(response => {
        // Aborted requests resolve to "canceled", not a missing facility.
        if (typeof response !== "object" || response === null) {
          if (response !== "canceled") setNotFound(true);
          return;
        }

        const {status, data} = response;
        if (status === 200 && data) {
          setFacility(data);
          getNotifications();
        } else {
          setNotFound(true);
        }
      });
    }
  }, [currentUser, facility, apiFacility, setFacility, getNotifications, slug]);

  if (notFound) return <Navigate to="/facilities" />;

  return (
    <Page hasMenu>
      <Room
        name="home"
        active={active}
        setActive={setActive}
        withLocks
        shouldJoinRoom
      />

      {facility?.id ? (
        <>
          <FacilityPageHeader
            facility={facility}
            reloadData={() => {
              setFacility(null);
              setAnalytics(null);
            }}
            path={tabPath}
          />

          <Dashboard>
            {(roleCanAccessResource("facility_information", "view") ||
              (roleCanAccessResource("facility_address", "view") && facility.primaryAddress)) &&
              facility?.type && (
                <Left>
                  {facility?.details && facility.details !== undefined && (
                    <>
                      {roleCanAccessResource("facility_information", "view") && (
                        <Details
                          details={{
                            region: facility.region?.name?.toUpperCase(),
                            ...facility.details,
                            address: facility.primaryAddress
                          }}
                          control={
                            roleCanAccessResource("facility_information", "update") &&
                            !facility.isDeleted && (
                              <Edit type="button" onClick={() => setShowEditDetails(true)}>
                                <FontAwesomeIcon icon={faEdit} />
                              </Edit>
                            )
                          }
                        />
                      )}
                      {roleCanAccessResource("facility_address", "view") &&
                        facility.primaryAddress && (
                          <PrimaryAddress
                            details={{...facility.details, address: facility.primaryAddress}}
                          />
                        )}
                      {roleCanAccessResource("facility_information", "view") && facility?.type && (
                        <Contacts
                          details={{...facility.details, address: facility.primaryAddress}}
                          control={
                            roleCanAccessResource("facility_information", "update") &&
                            !facility.isDeleted && (
                              <Edit type="button" onClick={() => setShowEditContacts(true)}>
                                <FontAwesomeIcon icon={faEdit} />
                              </Edit>
                            )
                          }
                        />
                      )}
                    </>
                  )}
                </Left>
              )}
            <Main
              fullWidth={
                (!roleCanAccessResource("facility_information", "view") &&
                  !(
                    roleCanAccessResource("facility_address", "view") && facility.primaryAddress
                  )) ||
                !facility.type
              }>
              <TabBar>
                <Tabs
                  count={
                    currentUser?.role.permissions.page.byId.facility_dashboard.children.filter(
                      key => currentUser?.role.permissions.page.byId[key].visible
                    ).length
                  }>
                  {facility && !facility?.type && (
                    <TabLink to="" active={pathname.match(`^/facilities/${slug}/?$`) ? 1 : 0}>
                      <span>Contacts</span>
                    </TabLink>
                  )}
                  {roleCanAccessPage("analytics") && roleCanAccessResource("analytic", "view") && (
                    <TabLink
                      to={facility && !facility.type ? "analytics" : ""}
                      active={
                        pathname.match(
                          `^/facilities/${slug}/${facility && !facility?.type ? "analytics/" : ""}?$`
                        )
                          ? 1
                          : 0
                      }>
                      <span>{facility?.type ? "Overview" : "Analytics"}</span>
                    </TabLink>
                  )}
                  {roleCanAccessPage("records") &&
                    roleCanAccessResource("facility_checksheet", "view") && (
                      <TabLink
                        to="checksheets"
                        active={pathname.match(`^/facilities/${slug}/checksheets/?$`) ? 1 : 0}>
                        <span>Records</span>
                      </TabLink>
                    )}
                  {roleCanAccessPage("reports") && roleCanAccessResource("report", "view") && (
                    <TabLink
                      to="reports"
                      active={pathname.match(`^/facilities/${slug}/reports/?$`) ? 1 : 0}>
                      <span>Reports</span>
                    </TabLink>
                  )}
                  {roleCanAccessPage("documents") &&
                    roleCanAccessResource("custom_table", "view") && (
                      <TabLink
                        to="tables"
                        active={pathname.match(`^/facilities/${slug}/tables/?$`) ? 1 : 0}>
                        <span>Documents</span>
                      </TabLink>
                    )}
                  {roleCanAccessPage("users") &&
                    roleCanAccessResource("user", "view") &&
                    facility.type && (
                      <TabLink
                        to="users"
                        active={pathname.match(`^/facilities/${slug}/users/?$`) ? 1 : 0}>
                        <span>Users</span>
                      </TabLink>
                    )}
                </Tabs>
                <TabActions
                  ref={setActionMenuEl}
                  $visible={
                    pathname === `/facilities/${slug}/checksheets` ||
                    pathname === `/facilities/${slug}/tables` ||
                    pathname === `/facilities/${slug}/reports` ||
                    (roleCanAccessPage("analytics") &&
                      roleCanAccessResource("analytic", "view") &&
                      pathname ===
                        `/facilities/${slug}${facility && !facility.type ? "/analytics" : ""}`)
                  }
                />
              </TabBar>

              {facility && !facility.type && pathname === `/facilities/${slug}` && (
                <ContactsPage facility={facility} />
              )}
              {roleCanAccessPage("analytics") &&
                roleCanAccessResource("analytic", "view") &&
                pathname ===
                  `/facilities/${slug}${facility && !facility.type ? "/analytics" : ""}` && (
                  <Analytics facility={facility} actionMenuEl={actionMenuEl} />
                )}
              {roleCanAccessPage("records") &&
                roleCanAccessResource("facility_checksheet", "view") &&
                pathname === `/facilities/${slug}/checksheets` && (
                  <Checksheets
                    facilityId={facility.id}
                    locks={locks}
                    actionMenuEl={actionMenuEl}
                  />
                )}
              {pathname === `/facilities/${slug}/reports` && (
                <Reports facilityId={facility.id} actionMenuEl={actionMenuEl} />
              )}
              {roleCanAccessPage("documents") &&
                roleCanAccessResource("custom_table", "view") &&
                pathname === `/facilities/${slug}/tables` && (
                  <CustomTables facility={facility} actionMenuEl={actionMenuEl} />
                )}
              {roleCanAccessPage("users") &&
                roleCanAccessResource("user", "view") &&
                pathname === `/facilities/${slug}/users` && (
                  <TableFacilityUsers facilityId={facility.id} />
                )}
            </Main>

            {showDetails && (
              <Modal visible={showDetails} setVisible={setShowDetails}>
                <Details
                  details={{
                    region: facility.region?.name?.toUpperCase(),
                    ...facility.details
                  }}
                  fullWidth
                />
              </Modal>
            )}

            {showEditDetails && (
              <ModalEditDetails
                visible={showEditDetails}
                setVisible={setShowEditDetails}
                facility={facility}
                setFacility={setFacility}
              />
            )}

            {showEditContacts && (
              <ModalEditContacts
                visible={showEditContacts}
                setVisible={setShowEditContacts}
                facility={facility}
                setFacility={setFacility}
                setRefreshFacility={() => {
                  // Force a refetch of the current facility (same slug), so
                  // clear the guard that normally skips already-loaded slugs.
                  requestedSlug.current = null;
                  setFacility(null);
                }}
              />
            )}
          </Dashboard>
        </>
      ) : (
        <NotLoaded>
          <Loader data-testid="facilityDashboard.loader" />
        </NotLoaded>
      )}
    </Page>
  );
};

// Style Overrides
const Dashboard = styled.section`
  display: flex;
  flex-direction: column;
  margin-top: ${pad}px;

  ${bp(3)} {
    display: flex;
    flex-direction: row;
  }
`;

const Left = styled.section`
  ${bp(3)} {
    width: 30%;
    padding-right: ${pad / 2}px;
  }
`;

const Main = styled.section`
  margin-bottom: ${pad}px;

  ${bp(3)} {
    width: ${({fullWidth}) => (fullWidth ? "100%" : "70%")};
    padding-left: ${pad / 2}px;
  }
`;

const TabLink = styled(NavLink)`
  ${voice.quiet};
  text-align: center;
  padding: ${pad / 2}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
  color: ${({theme}) => theme.secondary};
  background-color: transparent;
  transition: ${transition};

  &:hover {
    background-color: ${({theme}) => theme.secondary};
    color: ${({theme}) => theme.tertiary};
  }

  ${bp(5)} {
    min-width: 120px;
    ${voice.normal};
  }

  ${({active, theme}) =>
    active &&
    css`
      background-color: ${theme.secondary};
      color: ${theme.tertiary};
    `};

  span {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
    display: block;
  }
`;

const TabBar = styled.div`
  ${flex("row", "nowrap", "start", "center")};
  margin-bottom: ${pad}px;
  gap: ${pad / 2}px;
  width: 100%;
`;

const Tabs = styled.div`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 2}px;
  flex: 1;
  min-width: 0;

  ${TabLink} {
    width: ${({count}) => (count && count > 2 ? `${100 / count}%` : "110px")};
  }
`;

const TabActions = styled.div`
  flex: 0 0 auto;
  align-self: stretch;
  display: ${({$visible}) => ($visible ? "flex" : "none")};
  align-items: stretch;
  min-width: ${({$visible}) => ($visible ? "36px" : "0")};
`;

const Edit = styled.button`
  position: absolute;
  top: ${pad / 2}px;
  right: ${pad}px;
  width: fit-content;
  z-index: ${z("base") + 1};

  svg {
    fill: ${({theme}) => theme.secondary};
    padding: ${pad / 2}px 0 ${pad}px ${pad}px;
  }
`;

export default FacilityDashboard;
